import {
  CalendarDate,
  parseAbsoluteToLocal,
  toCalendarDate,
  toTime,
} from '@internationalized/date';
import { useCallback, useEffect, useState } from 'react';
import type { TimeValue } from 'react-aria-components';

import { DatePicker } from '@/lib/base/pickers/date-picker/date-picker';
import { useOrgTimeFormat } from '@/lib/time';

import type { PropertyInputProps } from './TextInput';

/**
 * DateTimeInput — a single DatePicker in `showTime` mode: the calendar
 * popover carries an integrated time field (with a Done button), and the
 * trigger shows a combined "Jun 5, 2026 · 11:00 AM" label. One control,
 * not a date picker crammed beside a separate time field.
 * Emits ISO 8601 strings (e.g. "2026-03-09T14:30:00.000Z"); a date picked
 * without a time is emitted at local midnight, matching prior behaviour.
 */
export const DateTimeInput: React.FC<PropertyInputProps> = ({
  propertyType,
  value,
  onChange,
  disabled = false,
  autoOpenPicker,
  onPickerOpenChange,
  size = 'sm',
}) => {
  const { hourCycle } = useOrgTimeFormat();
  const stringValue = value as string | null | undefined;

  // Parse ISO string into date and time parts
  const parsed = stringValue
    ? (() => {
        try {
          return parseAbsoluteToLocal(stringValue);
        } catch {
          return null;
        }
      })()
    : null;

  const dateValue = parsed ? toCalendarDate(parsed) : null;
  const timeValue = parsed ? toTime(parsed) : null;

  // Track time locally so it persists when user changes only the date
  const [localTime, setLocalTime] = useState<TimeValue | null>(timeValue);

  // Sync localTime whenever the controlled value prop changes
  useEffect(() => {
    setLocalTime(timeValue);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- timeValue is derived from stringValue (via parsed); including it causes render thrashing since toTime() creates a new object each render
  }, [stringValue]);

  // Combine date + time into ISO string and emit. Cleared values emit null —
  // the events API clears a property with a null value; an empty string is
  // rejected by the datetime field type's validation.
  const emitChange = useCallback(
    (date: CalendarDate | null, time: TimeValue | null) => {
      if (!date) {
        onChange(null);
        return;
      }
      // Build a Date from the calendar date + optional time
      const hours = time ? time.hour : 0;
      const minutes = time ? time.minute : 0;
      const seconds = time && 'second' in time ? time.second : 0;
      const jsDate = new Date(
        date.year,
        date.month - 1,
        date.day,
        hours,
        minutes,
        seconds
      );
      onChange(jsDate.toISOString());
    },
    [onChange]
  );

  const handleDateChange = useCallback(
    (newDate: { year: number; month: number; day: number } | null) => {
      if (!newDate) {
        onChange(null);
        return;
      }
      const calDate = new CalendarDate(
        newDate.year,
        newDate.month,
        newDate.day
      );
      emitChange(calDate, localTime);
    },
    [emitChange, localTime, onChange]
  );

  const handleTimeChange = useCallback(
    (newTime: TimeValue | null) => {
      setLocalTime(newTime);
      if (dateValue) {
        emitChange(dateValue, newTime);
      }
    },
    [dateValue, emitChange]
  );

  return (
    <DatePicker
      value={dateValue}
      onChange={handleDateChange}
      showTime
      timeValue={localTime}
      onTimeChange={handleTimeChange}
      isDisabled={disabled}
      isRequired={propertyType.is_required}
      // Optional datetimes get a "Clear date" action in the popover footer —
      // without it a set value could never be removed.
      clearable={!propertyType.is_required}
      className='w-full'
      // Table-cell editing renders the compact `xs` trigger (matching the
      // other cell editors), a tighter calendar popover, and opens the
      // calendar immediately — entering edit mode IS the intent to pick.
      // Every other host gets the caller's `size` (default `sm`).
      size={autoOpenPicker ? 'xs' : size}
      defaultOpen={autoOpenPicker}
      onOpenChange={onPickerOpenChange}
      aria-label={propertyType.label}
      hourCycle={hourCycle}
    />
  );
};
